import React from 'react';
import { IContact } from '../../models/Company';
import {
    CommandBar,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    ICommandBarItemProps,
    IContextualMenuItem,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    PanelType,
    PrimaryButton,
    Stack,
    TextField,
    Toggle
} from '@fluentui/react';
import {toast} from 'react-toastify';
import StorefrontDropdown from '../Storefront/StorefrontDropdown';
import CompanyDropdown from './CompanyDropdown';
import {useDeleteContactMutation, useGetCompanyQuery, useGetLocationsForCompanyQuery, useSaveContactMutation} from '../../store/Api';
import {locationLabel} from './CompanyLocationsDisplay';
import SearchFilter from "../Common/SearchFilter";
import ExternalMappingsDialog from "./ExternalMappingsDialog";
import {useGetContactLinksQuery} from '../../store/endpoints/RecordLinksEndpoints';
import StandardPanel from '../Common/StandardPanel';
import Setting from '../Common/Setting';

interface IContactDetailsModal {
    isOpen: boolean;
    /** `contactId` is the saved contact's id (new or existing) when `saved` is true. */
    onDismiss: (saved: boolean, contactId?: string) => void;
    contact?: IContact;
    /** Pre-selects the company for a new contact (e.g. adding one from a prepared cart). */
    defaultCompany?: string;
    /** Pass false to dock beside the page without an overlay. */
    isBlocking?: boolean;
}

const ContactDetailsModal: React.FC<IContactDetailsModal> = (props) => {
    const [saveContact] = useSaveContactMutation();
    const [deleteContact] = useDeleteContactMutation();
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState<boolean>(false);
    const [saving, setSaving] = React.useState<boolean>(false);
    const [firstName, setFirstName] = React.useState<string | undefined>(props.contact?.firstName);
    const [lastName, setLastName] = React.useState<string | undefined>(props.contact?.lastName);
    const [email, setEmail] = React.useState<string | undefined>(props.contact?.emailAddress);
    const [phone, setPhone] = React.useState<string | undefined>(props.contact?.phoneNumber);
    const [company, setCompany] = React.useState<string | undefined>(props.contact?.company ?? props.defaultCompany);
    const [defaultStorefront, setDefaultStorefront] = React.useState<string | undefined>(props.contact?.storefront);
    const [externalId, setExternalId] = React.useState<string | undefined>(props.contact?.externalId);
    const [externalIdType, setExternalIdType] = React.useState<string | undefined>(props.contact?.externalIdType);
    const [companyName, setCompanyName] = React.useState<string | undefined>(props.contact?.company);
    const [companyError, setCompanyError] = React.useState<string | undefined>();
    const [storefronts, setStorefronts] = React.useState<string[] | undefined>(props.contact?.storefronts);
    const [externalIdMappings, setExternalIdMappings] = React.useState<{[p: string]: string}>(props.contact?.externalMappings ?? {});
    const [procurementShippingContact, setProcurementShippingContact] = React.useState<boolean>(props.contact?.procurementShippingContact ?? false);
    const [procurementBillingContact, setProcurementBillingContact] = React.useState<boolean>(props.contact?.procurementBillingContact ?? false);
    const [defaultLocationId, setDefaultLocationId] = React.useState<string | undefined>(props.contact?.defaultLocationId);
    const [showModal, setShowModal] = React.useState<boolean>(false);
    React.useEffect(() => {
        setFirstName(props.contact?.firstName);
        setLastName(props.contact?.lastName);
        setEmail(props.contact?.emailAddress);
        setPhone(props.contact?.phoneNumber);
        setCompany(props.contact?.company ?? props.defaultCompany);
        setDefaultStorefront(props.contact?.storefront);
        setExternalId(props.contact?.externalId);
        setExternalIdType(props.contact?.externalIdType);
        setCompanyName(props.contact?.companyName);
        setExternalIdMappings(props.contact?.externalMappings ?? {})
        setProcurementShippingContact(props.contact?.procurementShippingContact ?? false);
        setProcurementBillingContact(props.contact?.procurementBillingContact ?? false);
        setDefaultLocationId(props.contact?.defaultLocationId);
    }, [props.contact, props.defaultCompany])

    const companyLocations = useGetLocationsForCompanyQuery({companyId: company ?? ''}, {skip: !company});
    const selectedCompany = useGetCompanyQuery(company ?? '', {skip: !company});
    const locationOptions: IDropdownOption[] = [
        {key: '', text: 'Use company default'},
        ...(companyLocations.data?.items ?? []).map(l => ({key: l.id ?? '', text: locationLabel(l)}))
    ];

    React.useEffect(() => {
        if(defaultStorefront && defaultStorefront != '' && defaultStorefront != 'default' && !props.contact?.storefronts?.includes(defaultStorefront)){
            setStorefronts([...(props.contact?.storefronts ?? []), defaultStorefront]);
        }
        else{
            setStorefronts(props.contact?.storefronts);
        }
    }, [props.contact?.storefronts, defaultStorefront])

    const save = () => {
        if(company === undefined){
            setCompanyError("Company is required")
        }
        else {
            setSaving(true);
            saveContact({
                id: props.contact?.id,
                firstName,
                lastName,
                emailAddress: email ?? '',
                phoneNumber: phone,
                company,
                storefront: defaultStorefront,
                externalId,
                externalIdType,
                companyName: companyName,
                storefronts: storefronts,
                externalMappings: externalIdMappings,
                defaultLocationId: defaultLocationId || undefined,
                procurementShippingContact,
                procurementBillingContact
            }).then((result) => {
                setSaving(false);
                const savedId = 'data' in result ? result.data?.id ?? undefined : undefined;
                props.onDismiss(true, props.contact?.id ?? savedId);
            });
        }
    }


    // The footer's surface is painted by StandardPanel, so this only lays the buttons out.
    const footer = () => <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton text='Save' onClick={save} disabled={saving} />
                <DefaultButton text='Cancel' onClick={()=>props.onDismiss(false)} />
            </Stack>
    const links = useGetContactLinksQuery(props.contact?.id ?? '', {skip: !props.contact?.id});

    // Delete the contact (saved records only). Confirmed via a dialog; the list refetches on dismiss.
    const onDelete = async () => {
        setShowDeleteConfirm(false);
        if (!props.contact?.id) return;
        const name = [props.contact.firstName, props.contact.lastName].filter(Boolean).join(' ') || props.contact.emailAddress || 'contact';
        const toastId = toast.loading(`Deleting ${name}…`, {closeButton: false, closeOnClick: false, draggable: false});
        const resolved = {isLoading: false, autoClose: 4000, closeButton: true, closeOnClick: true, draggable: true};
        try {
            const res = await deleteContact(props.contact.id).unwrap();
            if (res.success === false) throw new Error(res.message);
            toast.update(toastId, {type: 'success', render: `${name} deleted`, ...resolved});
            props.onDismiss(true);
        } catch {
            toast.update(toastId, {type: 'error', render: `Failed to delete ${name}`, ...resolved});
        }
    };

    // "Open in CRM" lives in the three-dots header menu (consistent with the company/location editors).
    const commands: ICommandBarItemProps[] = [{
        key: 'new',
        text: 'Add Mapping',
        iconProps: { iconName: 'Add' },
        onClick: () => {setShowModal(true);},
    }];
    // Delete lives in the header three-dots menu (saved contacts only).
    const menuItems: IContextualMenuItem[] = props.contact?.id ? [{
        key: 'delete-contact',
        text: 'Delete Contact',
        iconProps: {iconName: 'Delete'},
        onClick: () => { setShowDeleteConfirm(true); }
    }] : [];
    const headerTitle = props.contact ? props.contact?.firstName + ' ' + props.contact?.lastName : 'New Contact';
    return (
        <StandardPanel
            type={PanelType.smallFixedFar}
            isBlocking={props.isBlocking}
            headerText={headerTitle}
            links={links.data}
            menuItems={menuItems}
            isOpen={props.isOpen}
            onDismiss={()=>props.onDismiss(false)}
            isFooterAtBottom={true}
            onRenderFooterContent={footer}>
            <Dialog
                hidden={!showDeleteConfirm}
                onDismiss={() => setShowDeleteConfirm(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Delete contact',
                    subText: `Permanently delete ${[props.contact?.firstName, props.contact?.lastName].filter(Boolean).join(' ') || props.contact?.emailAddress || 'this contact'}? This can't be undone.`
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={onDelete} text='Delete' styles={{root: {backgroundColor: '#db2828', borderColor: '#db2828'}}} />
                    <DefaultButton onClick={() => setShowDeleteConfirm(false)} text='Cancel' />
                </DialogFooter>
            </Dialog>
            <ExternalMappingsDialog
                onClose={() => setShowModal(false)}
                display={showModal}
                onAdd={(a, b) => {
                setExternalIdMappings({...externalIdMappings, [a]: b});
                setShowModal(false);}}
            />

            {companyError && <MessageBar messageBarType={MessageBarType.error}>{companyError}</MessageBar>}
            <Setting id='contacts.contact.firstName'><TextField label='First Name' value={firstName} onChange={(e, v) => setFirstName(v)} /></Setting>
            <Setting id='contacts.contact.lastName'><TextField label='Last Name' value={lastName} onChange={(e, v) => setLastName(v)} /></Setting>
            <Setting id='contacts.contact.email'><TextField label='Email' value={email} onChange={(e, v) => setEmail(v)} /></Setting>
            <Setting id='contacts.contact.phone'><TextField label='Phone' value={phone} onChange={(e, v) => setPhone(v)} /></Setting>
            <CompanyDropdown selected={company} required={true} onChange={(option) => {
                setCompany(option?.key as string);
                setCompanyName(option?.text);
            }} />
            {selectedCompany.data?.disabled &&
                <MessageBar messageBarType={MessageBarType.severeWarning}>
                    {`${selectedCompany.data.companyName || 'This company'} is disabled. Contacts of a disabled company cannot log in to any storefront, even ones they are assigned to.`}
                </MessageBar>}
            <StorefrontDropdown
                useId
                showMultiSelect={true}
                selected={defaultStorefront}
                label={'Default Storefront'}
                onChange={(selected) => setDefaultStorefront(selected)}
                inheritedStorefrontId={props.contact?.companyStorefront}
                onChangeStorefronts={(storefronts) => {setStorefronts(storefronts);}}
                contactStorefronts={storefronts}
            />
            <Setting id='contacts.contact.defaultLocation'>
            <Dropdown
                label='Default Location'
                selectedKey={defaultLocationId ?? ''}
                options={locationOptions}
                onChange={(e, option) => setDefaultLocationId(option?.key ? String(option.key) : undefined)}
                disabled={!company}
                title={!company ? 'Select a company first' : undefined}
            />
            </Setting>

            <h3>Procurement</h3>
            <Setting id='contacts.contact.procurementShippingContact'>
            <Toggle
                label='Procurement Shipping Contact'
                checked={procurementShippingContact}
                onChange={(e, checked) => setProcurementShippingContact(!!checked)}
                onText='Selectable as a ship-to on non-dropship procurement orders'
                offText='Not selectable as a procurement ship-to'
            />
            </Setting>
            <Setting id='contacts.contact.procurementBillingContact'>
            <Toggle
                label='Procurement Billing Contact'
                checked={procurementBillingContact}
                onChange={(e, checked) => setProcurementBillingContact(!!checked)}
                onText='Selectable as a bill-to on procurement orders'
                offText='Not selectable as a procurement bill-to'
            />
            </Setting>

            <h3>External ID Mappings</h3>
            <Setting id='contacts.contact.externalId'><TextField label='Default External ID' value={externalId} onChange={(e, v) => setExternalId(v)} /></Setting>
            <Setting id='contacts.contact.externalIdType'><TextField label='Default External ID Type' value={externalIdType} onChange={(e, v) => setExternalIdType(v)} /></Setting>
            {externalIdMappings && Object.keys(externalIdMappings).length > 0 &&
            <div style={{display: "inline-flex", flexWrap: "wrap"}}>
                {Object.keys(externalIdMappings).map((key) => (
                    <div key={key} style={{margin: '5px'}}>
                        <SearchFilter onDelete={() => {
                            const newMappings = {...externalIdMappings};
                            delete newMappings[key];
                            setExternalIdMappings(newMappings)
                        }}>
                            <b>{key}:</b> {externalIdMappings[key]}
                        </SearchFilter>
                    </div>
                ))}

            </div>}
            <CommandBar
                items={commands}
                farItems={[]}
            />
        </StandardPanel>
    );
}

export default ContactDetailsModal;